import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Globe2, ChevronRight } from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import RelatedLinks from '../components/RelatedLinks';

// Only countries the product actually supports today.
const COUNTRIES = [
  { flag: '🇺🇸', name: 'United States', framework: 'Self-employment tax + IRS Schedule C, quarterly 1040-ES estimates', currency: 'USD', to: '/tools/1099-tax-calculator', reports: 'Schedule C category summary, P&L, receipt archive' },
  { flag: '🇬🇧', name: 'United Kingdom', framework: 'Self Assessment income tax + Class 4 National Insurance', currency: 'GBP', to: '/tools/uk-self-assessment-tax-calculator', reports: 'Self Assessment-ready income & expense summary' },
  { flag: '🇨🇦', name: 'Canada', framework: 'Self-employed income on the T2125, federal + provincial tax, CPP', currency: 'CAD', to: '/tools/canada-self-employed-tax-calculator', reports: 'T2125 category summary, P&L' },
  { flag: '🇩🇪', name: 'Germany', framework: 'Einkommensteuer (income tax) + 19% VAT (Umsatzsteuer)', currency: 'EUR', to: '/tools/germany-self-employed-tax-calculator', reports: 'VAT summary, income & expense report' },
  { flag: '🇦🇪', name: 'United Arab Emirates', framework: '5% VAT (FTA), AED 375,000 registration threshold', currency: 'AED', to: '/tools/uae-vat-calculator', reports: 'VAT-ready summary with TRN capture' },
  { flag: '🇸🇦', name: 'Saudi Arabia', framework: '15% VAT (ZATCA), SAR 375,000 registration threshold', currency: 'SAR', to: '/tools/saudi-arabia-vat-calculator', reports: 'VAT-ready summary with tax number capture' },
];

const ALSO = 'Australia, New Zealand, Ireland, the Netherlands, Spain, France, Italy, Portugal, Belgium, Switzerland and Poland';

const FAQS = [
  { q: 'Which countries does MoneyFeed support?', a: 'MoneyFeed has dedicated tax support for the United States (Schedule C / 1040-ES), United Kingdom (Self Assessment), Canada (T2125), Germany, the UAE (5% VAT) and Saudi Arabia (15% VAT / ZATCA). It also has free tax calculators and guides for Australia, New Zealand and several European countries, with multi-currency tracking used everywhere else.' },
  { q: 'Does MoneyFeed work with multiple currencies?', a: 'Yes. You can record income and expenses in your local currency, and reports use that currency. This is useful for freelancers who invoice clients abroad.' },
  { q: 'Can I use MoneyFeed if my country isn’t listed?', a: 'Yes. Even without a dedicated tax framework, MoneyFeed still tracks and categorizes your income and expenses in your currency and exports clean CSV/Excel reports you can hand to a local accountant.' },
  { q: 'Does MoneyFeed replace professional tax advice?', a: 'No. MoneyFeed keeps your books organized and tax-ready and provides estimates, but it does not replace advice from a qualified accountant or tax professional in your country.' },
];

export default function SupportedCountries() {
  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'WebPage', name: 'Countries MoneyFeed Supports', url: 'https://moneyfeed.io/supported-countries', description: 'The countries and tax frameworks MoneyFeed supports — US Schedule C, UK Self Assessment, Canada T2125, Germany, and UAE & Saudi Arabia VAT, plus multi-currency tracking.' },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#ECFDF5] to-[#D1FAE5]" data-testid="supported-countries-page">
      <Seo
        title="Countries MoneyFeed Supports — Tax Frameworks & Currencies"
        description="See the countries MoneyFeed supports: US Schedule C, UK Self Assessment, Canada T2125, Germany, and UAE & Saudi Arabia VAT — plus multi-currency tracking everywhere."
        path="/supported-countries"
        jsonLd={jsonLd}
      />
      <LandingHeader />

      <nav className="max-w-6xl mx-auto px-4 sm:px-8 pt-6 text-xs text-gray-500 flex items-center gap-1" aria-label="Breadcrumb" data-testid="countries-breadcrumb">
        <Link to="/" className="hover:text-[#064E3B]">Home</Link>
        <ChevronRight className="h-3 w-3" /> <span className="text-[#064E3B] font-semibold">Supported Countries</span>
      </nav>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 pt-8 pb-6 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#84CC16]/15 rounded-full mb-5">
          <Globe2 className="h-4 w-4 text-[#65A30D]" />
          <span className="text-xs font-bold text-[#65A30D] uppercase tracking-wider">Global Coverage</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-[#064E3B] leading-tight mb-5">Countries MoneyFeed supports</h1>
        <p className="text-base sm:text-lg text-gray-700 max-w-2xl mx-auto">
          MoneyFeed has dedicated tax support for the countries below and multi-currency tracking everywhere. Each supported country maps your expenses to the right local tax lines.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {COUNTRIES.map((c) => (
            <div key={c.name} className="bg-white rounded-2xl shadow-md p-6" data-testid={`country-${c.name}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl" aria-hidden="true">{c.flag}</span>
                <div>
                  <h2 className="font-bold text-[#064E3B]">{c.name}</h2>
                  <span className="text-xs font-semibold text-[#65A30D]">Currency: {c.currency}</span>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-2"><span className="font-semibold text-[#064E3B]">Tax framework:</span> {c.framework}</p>
              <p className="text-sm text-gray-600 mb-4"><span className="font-semibold text-[#064E3B]">Reports:</span> {c.reports}</p>
              <Link to={c.to} className="inline-flex items-center gap-1 text-sm font-semibold text-[#065F46] hover:underline" data-testid={`country-link-${c.name}`}>
                Open {c.name} tax calculator <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-gray-600 mt-8 max-w-2xl mx-auto">
          Free tax calculators and guides are also available for {ALSO}. Everywhere else, MoneyFeed still tracks income and expenses in your currency and exports accountant-ready reports.
        </p>
        <p className="text-center text-xs text-gray-400 mt-4 max-w-2xl mx-auto">
          MoneyFeed keeps your books tax-ready but does not replace professional tax advice. Rates and thresholds are reviewed against official sources — see our <Link to="/tax-support" className="underline">Tax Support</Link> page. Last reviewed June 2026.
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">Country & currency FAQ</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`countries-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <div className="bg-[#064E3B] rounded-3xl p-10 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Tax-ready wherever you work</h2>
          <p className="text-[#A7F3D0] mb-6 max-w-xl mx-auto">Start free and keep your books ready for your country’s tax rules.</p>
          <Link to="/auth">
            <Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-8 py-6 text-base" data-testid="countries-cta-signup">
              Get started free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      <RelatedLinks />

      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
          <Link to="/tax-support" className="hover:text-[#064E3B]">Tax Support</Link>
          <Link to="/features" className="hover:text-[#064E3B]">Features</Link>
          <Link to="/about-moneyfeed" className="hover:text-[#064E3B]">About</Link>
          <Link to="/blog" className="hover:text-[#064E3B]">Blog</Link>
        </div>
      </footer>
    </div>
  );
}
